import React, { useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import { observable } from 'mobx';

class BaseSwiperStore {
  @observable
  swiperOptions = {
    loop: true,
    spaceBetween: 0,
    autoplay: {
      disableOnInteraction: false,
      delay: 4000
    }
  };

  @observable activeIndex = 0;
  @observable size = 0;

  constructor({ ...rest } = {}) {
    Object.assign(this, rest);
  }

  goNext = () => {
    const swiper = this.swiperRef.current.swiper;
    swiper.slideNext();
  };

  goPrev = () => {
    const swiper = this.swiperRef.current.swiper;
    swiper.slidePrev();
  };

  onChange = activeIndex => {
    this.activeIndex = activeIndex;
  };

  isDisabled() {
    return this.size < 2;
  }

  use({ children }) {
    this.size = React.Children.count(children);

    const swiperRef = (this.swiperRef = useRef());
    this.onResize = debounce(() => {
      swiperRef.current?.swiper.update();
    }, 500);

    useEffect(
      () => {
        const onResize = this.onResize;
        window.addEventListener('resize', onResize);
        return () => {
          window.removeEventListener('resize', onResize);
        };
      },
      [this.onResize]
    );

    useEffect(() => {
      const swiper = this.swiperRef.current.swiper;
      const context = this;

      // 监听slideChange事件，更新activeIndex
      const handleSlideChange = function() {
        context.activeIndex = swiper.realIndex;
      };

      // 监听transitionEnd事件，确保loop切换完成后状态正确
      // 这个事件在loop模式下特别重要，因为slideChange可能在切换过程中触发
      const handleTransitionEnd = function() {
        // 确保activeIndex和realIndex同步
        if (context.activeIndex !== swiper.realIndex) {
          context.activeIndex = swiper.realIndex;
        }
        // 强制更新Swiper，确保.swiper-slide-active类正确
        swiper.update();
      };

      swiper.on('slideChange', handleSlideChange);
      swiper.on('transitionEnd', handleTransitionEnd);

      return () => {
        swiper.off('slideChange', handleSlideChange);
        swiper.off('transitionEnd', handleTransitionEnd);
      };
    }, []);

    useEffect(
      () => {
        const swiper = this.swiperRef.current?.swiper;
        if (swiper) {
          if (swiper.realIndex !== this.activeIndex) {
            // 如果realIndex不匹配，切换slide
            swiper.slideToLoop(this.activeIndex);
          } else {
            // 即使realIndex匹配，也强制更新Swiper，确保.swiper-slide-active类正确
            // 这在loop模式下点击真实节点时特别重要
            swiper.update();
          }
        }
      },
      [this.activeIndex]
    );
  }
}

export default BaseSwiperStore;
